import * as React from "react";
import {
  Column,
  TableCellRenderer
} from "react-table";
import _ from "lodash";
import {
  CellContext,
  ColumnDef,
  createColumnHelper
} from "@tanstack/react-table";
import { $enum } from "ts-enum-util";

import { InstalledModuleType } from "src/utils/constants/global-constants";
import {
  IOrderStatus,
  IOrder,
  OrderPaymentStatus
} from "src/core/orders/api/orders.api";
import * as globalTableColumns from "src/core/shared/components/TableGlobalColumns";
import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import { OrderPaymentStatusText } from "src/modules/payment/components";
import { orderPaymentStatusToHuman } from "src/modules/payment/utils";
import { customCreateColumnHelper } from "src/core/dashboard/components/widgets/table/customColumnHelper";
import { TotalPrice } from "src/core/prices/components/TotalPrice";

import { orderPaymentStatusColumn } from "../TableGlobalColumns";

export interface IOrderTableNewColumns {
  actionButton: (info: CellContext<IOrder, unknown>) => React.ReactNode;
  orderStatuses: IOrderStatus[];
  terminology: ITerminology;
  installedModules: InstalledModuleType[];
  pricesVisible: boolean;
  // Enables the ability to request specific columns as apposed to all
  selectedColumns?: OrderAvailableTableColumn[];
  // For overiding default filterable behaviour. Add keys as needed in OrderTableNonFilterableColumns
  nonFilterableColumns?: OrderTableNonFilterableColumns;
}

export type OrderAvailableTableColumn = keyof AvailableOrderColumns;
export type OrderTableNonFilterableColumns = Array<keyof Pick<IOrder, "customerReference">>;

// used to determine what columns can be rendered
interface AvailableOrderColumns {
  id: ColumnDef<IOrder, unknown>;
  createdAt: ColumnDef<IOrder, unknown>;
  updatedAt: ColumnDef<IOrder, unknown>;
  orderDeliveryDate: ColumnDef<IOrder, unknown>;
  customerReference: ColumnDef<IOrder, unknown>;
  invoiceNumber: ColumnDef<IOrder, unknown>;
  type: ColumnDef<IOrder, unknown>;
  orderStatus: ColumnDef<IOrder, unknown>;
  productCount: ColumnDef<IOrder, unknown>;
  cartTotal: ColumnDef<IOrder, unknown>;
  actions: ColumnDef<IOrder, unknown>;
  customer: ColumnDef<IOrder, unknown>;
  branch: ColumnDef<IOrder, unknown>
}

export const adminOrderTableNewColumns = (params: IOrderTableNewColumns): Array<ColumnDef<IOrder, unknown>> => {
  const customColumnHelper = customCreateColumnHelper<IOrder>();
  const columnHelper = createColumnHelper<IOrder>();
  let columns: Array<ColumnDef<IOrder, unknown>>;

  const availableColumns: AvailableOrderColumns = {
    id: customColumnHelper.numberColumn(columnHelper, "id", {
      header: "Id",
      size: 70,
      minSize: 70,
      enableColumnFilter: true
    }, { disableNumberFormatting: true }),
    customer: customColumnHelper.customerColumn(columnHelper, "branch.customer", { meta: { filter: { filterType: "string" } } }, params.terminology),
    branch: customColumnHelper.dropColumn(columnHelper, "branch", { meta: { filter: { filterType: "string" } } }, params.terminology),
    customerReference: customColumnHelper.standardTextColumn(columnHelper, "customerReference", {
      header: "PO Number",
      minSize: 100
    }),
    invoiceNumber: customColumnHelper.standardTextColumn(columnHelper, "invoiceNumber", {
      header: "Invoice Number",
      enableHiding: true,
      minSize: 100
    }),
    type: customColumnHelper.orderTypeColumn(columnHelper, "type", {
      meta: {
        filter: {
          filterType: "select",
          options: [
            {
              text: "Ad hoc",
              value: "adhoc"

            },
            {
              text: "Standing order",
              value: "scheduled"

            },
            {
              text: "Bulk Order",
              value: "bulk"

            }
          ]
        }
      }
    }),

    orderDeliveryDate: customColumnHelper.dateColumn(columnHelper, "orderDeliveryDate", {
      header: "Delivery Date",
      ignoreLocal: true
    }),
    productCount: customColumnHelper.productCountColumn(columnHelper, params.terminology),
    cartTotal: customColumnHelper.numberColumn(columnHelper, "orderLines", {
      header: "Total",
      cell: info => (
        <TotalPrice
          lines={info.row.original.orderLines}
          id={info.row.original.id}
          totalOnly
        />
      ),
      enableColumnFilter: false,
      enableSorting: false
    }),
    orderStatus: customColumnHelper.orderStatusColumn(columnHelper, "orderStatus", {
      enableSorting: false,
      meta: {
        filter: {
          filterType: "select",
          options: params.orderStatuses.map(status => ({
            text: status.name,
            value: status.id.toString()

          }))
        }
      }
    }
    ),
    createdAt: customColumnHelper.dateColumn(columnHelper, "createdAt", { header: "Created" }),
    updatedAt: customColumnHelper.dateColumn(columnHelper, "updatedAt", { header: "Updated" }),
    actions: customColumnHelper.actionColumn(columnHelper, params.actionButton, { size: 180 })
  };

  columns = Object.values(availableColumns);

  if (params.installedModules.includes("unity")) {
    columns.splice(4, 0, customColumnHelper.standardTextColumn(columnHelper, "orderReference", {
      header: "Order No.",
      minSize: 100
    }));
  }

  // only return selected columns if requested
  if (params.selectedColumns) {
    return params.selectedColumns.map(requestedColumn => availableColumns[ requestedColumn ]);
  }

  if (params.installedModules.includes("payment")) {
    columns.splice(columns.length - 1, 0, customColumnHelper.standardTextColumn(columnHelper, "paymentStatus", {
      header: "Payment Status",
      cell: info => {
        if (!info.cell.getValue()) {
          return "-";
        }

        return <OrderPaymentStatusText status={info.cell.getValue() as OrderPaymentStatus} />;
      },
      minSize: 100,
      meta: {
        filter: {
          filterType: "select",
          options: $enum(OrderPaymentStatus).getValues().map(status => {
            return ({
              text: orderPaymentStatusToHuman(status),
              value: status
            });
          })
        }
      }
    }));
  }

  if (!params.pricesVisible) {
    columns = _.filter(columns, col => col.header !== "Total");
  }

  return columns;
};

export interface IOrderTableColumns {
  terminology: ITerminology;
  ActionButton?: TableCellRenderer;
  orderStatuses: IOrderStatus[];
  installedModules: InstalledModuleType[];
}

export const adminOrderTableColumns = (params: IOrderTableColumns): Column[] => {
  const columns: Column[] = [
    globalTableColumns.id({ sortable: true }),
    globalTableColumns.customer(params.terminology),
    globalTableColumns.branch(params.terminology),
    globalTableColumns.standardText({
      Header: "PO Number",
      accessor: "customerReference",
      width: 100,
      sortable: false
    }),
    globalTableColumns.standardText({
      Header: "Invoice Number",
      accessor: "invoiceNumber",
      width: 100,
      sortable: true,
      show: false
    }),
    globalTableColumns.orderType(),
    // globalTableColumns.orderLinesCount(terminology),
    globalTableColumns.productCount(params.terminology),
    globalTableColumns.standardText({
      Header: "Total",
      accessor: "id",
      Cell: row => (
        <TotalPrice
          lines={row.original.orderLines}
          id={row.original.id}
          totalOnly
          discountValue={row.original.branch.discount}
        />
      ),
      width: 100,
      filterable: false
    }),
    globalTableColumns.date({
      header: "Created",
      accessor: "createdAt",
      sortable: true
    }),
    globalTableColumns.date({
      header: "Last Updated",
      accessor: "updatedAt",
      show: false,
      sortable: true
    }),
    globalTableColumns.date({
      header: "Delivery Date",
      accessor: "orderDeliveryDate",
      sortable: true
    }),
    globalTableColumns.orderStatus(params.orderStatuses),
    globalTableColumns.actions({
      cell: params.ActionButton,
      width: 200
    })
  ];

  if (params.installedModules.includes("payment")) {
    columns.splice(columns.length - 1, 0, orderPaymentStatusColumn);
  }

  if (params.installedModules.includes("unity")) {
    columns.splice(4, 0, globalTableColumns.standardText({
      Header: "Order No.",
      accessor: "orderReference",
      width: 100,
      sortable: true,
      show: true
    }));
  }

  return columns;
};
